import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-2e5716de"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 2,
  class: "help is-danger"
}
const _hoisted_2 = {
  key: 4,
  class: "help is-danger"
}
const _hoisted_3 = {
  key: 5,
  class: "checkbox-group"
}
const _hoisted_4 = {
  key: 1,
  class: "help is-danger"
}
const _hoisted_5 = {
  key: 6,
  class: "radio-group"
}
const _hoisted_6 = {
  key: 1,
  class: "help is-danger"
}
const _hoisted_7 = {
  key: 8,
  class: "help is-danger"
}

import { computed, watch } from 'vue'
import { isEmpty } from 'lodash'

import NrFormInput from '@nextrequestco/components/src/components/NrFormInput/nr-form-input.vue'
import NrEditor from '@nextrequestco/components/src/components/NrEditor/nr-editor.vue'
import NrDatepicker from '@nextrequestco/components/src/components/NrDatePicker/nr-datepicker.vue'

import {
  CheckboxOption,
  CheckboxSettings,
  FieldDefinition,
  FieldType,
  RadioOption,
  RadioSettings,
  RequestFieldValue,
  RichTextSettings,
  TextSettings,
  NumberSettings,
  DatepickerSettings
} from '../types/request_fields'
import striptags from 'striptags'

type TypedProps =
  | {
      field_type: FieldType.Text // eslint-disable-line camelcase
      type_settings: TextSettings // eslint-disable-line camelcase
      modelValue: string
    }
  | {
      field_type: FieldType.Number // eslint-disable-line camelcase
      type_settings: NumberSettings // eslint-disable-line camelcase
      modelValue: string
    }
  | {
      field_type: FieldType.RichText // eslint-disable-line camelcase
      type_settings: RichTextSettings // eslint-disable-line camelcase
      modelValue: string
    }
  | {
      field_type: FieldType.Checkbox // eslint-disable-line camelcase
      type_settings: CheckboxSettings // eslint-disable-line camelcase
      modelValue: CheckboxOption[]
    }
  | {
      field_type: FieldType.Radio // eslint-disable-line camelcase
      type_settings: RadioSettings // eslint-disable-line camelcase
      modelValue: RadioOption | null
    }
  | {
      field_type: FieldType.Datepicker // eslint-disable-line camelcase
      type_settings: DatepickerSettings // eslint-disable-line camelcase
      modelValue: string
    }


export default /*#__PURE__*/_defineComponent({
  __name: 'RequestFieldInput',
  props: {
    definition: {},
    modelValue: {}
  },
  emits: ["update:modelValue", "on-validate"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

// This combines the definition and modelValue into one type so that code can know the type of the `modelValue` depending on the `field_type`.
const typedProps = computed<TypedProps>(() => {
  let value = props.modelValue

  switch (props.definition.field_type) {
    case FieldType.Text:
      value = value || ''
      if (typeof value !== 'string') {
        throw new Error('Unexpected type')
      }
      return {
        field_type: props.definition.field_type,
        modelValue: value || '',
        type_settings: props.definition.type_settings
      }
    case FieldType.RichText:
      value = value || ''
      if (typeof value !== 'string') {
        throw new Error('Unexpected type')
      }
      return {
        field_type: props.definition.field_type,
        modelValue: value || '',
        type_settings: props.definition.type_settings
      }
    case FieldType.Number:
      value = value || ''
      if (typeof value !== 'string') {
        throw new Error('Unexpected type')
      }
      return {
        field_type: props.definition.field_type,
        modelValue: value,
        type_settings: props.definition.type_settings
      }
    case FieldType.Checkbox:
      value = value || []
      if (!Array.isArray(value)) {
        throw new Error('Unexpected type')
      }
      return {
        field_type: props.definition.field_type,
        modelValue: value,
        type_settings: props.definition.type_settings
      }
    case FieldType.Radio:
      if (Array.isArray(value)) {
        throw new Error('Unexpected type')
      }
      return {
        field_type: props.definition.field_type,
        modelValue: value as RadioOption | null,
        type_settings: props.definition.type_settings
      }
    case FieldType.Datepicker:
      value = value || ''
      if (typeof value !== 'string') {
        throw new Error('Unexpected type')
      }
      return {
        field_type: props.definition.field_type,
        modelValue: value || '',
        type_settings: props.definition.type_settings
      }
    default:
      throw new Error('Unexpected field type')
  }
})

const noLabel = computed(
  () =>
    props.definition.type_settings.hideLabel ||
    isEmpty(props.definition.type_settings.labelText)
)

const minDate = computed(() => {
  if (!props.definition.type_settings.allowPastDays) {
    const today = new Date()
    today.setDate(today.getDate() - 1)
    return today.toISOString()
  }
  return undefined
})

const minMaxErrorText = computed(() => {
  // eslint-disable-next-line camelcase
  const { type_settings, modelValue } = typedProps.value

  const strippedTextLength = striptags(modelValue).length

  return strippedTextLength < type_settings.characterLimitMin
})

const minMaxValueErrorText = computed(() => {
  // eslint-disable-next-line camelcase
  const { type_settings, modelValue } = typedProps.value

  const value = Number(modelValue)
  let message = ''
  if (value < type_settings.minValue) {
    message = `Minimum value of ${type_settings.minValue} required.`
  } else if (value > type_settings.maxValue) {
    message = `Maximum value of ${type_settings.maxValue} exceeded.`
  } else if (!type_settings.allowDecimals && value % 1 !== 0) {
    message = 'Decimals are not allowed.'
  }

  return message
})

const isChecked = (
  option: CheckboxOption | RadioOption,
  currentlyChecked: CheckboxOption[] | RadioOption | null
) => {
  if (Array.isArray(currentlyChecked)) {
    return currentlyChecked.some((checked) => checked.uuid === option.uuid)
  }
  return currentlyChecked?.uuid === option.uuid
}

const checkOption = (
  option: CheckboxOption | RadioOption,
  checked: boolean,
  currentlyChecked: CheckboxOption[] | RadioOption | null
) => {
  if (props.definition.field_type === FieldType.Checkbox) {
    const alreadyChecked = isChecked(
      option,
      currentlyChecked as CheckboxOption[]
    )
    if (alreadyChecked && !checked) {
      const selectedOptions: CheckboxOption[] = (
        currentlyChecked as CheckboxOption[]
      ).filter((other: CheckboxOption) => other.uuid !== option.uuid)
      emit('update:modelValue', selectedOptions)
    } else if (!alreadyChecked && checked) {
      const selectedOptions: CheckboxOption[] = [
        option as CheckboxOption,
        ...((currentlyChecked as CheckboxOption[]) || [])
      ]
      emit('update:modelValue', selectedOptions)
    }
  } else if (props.definition.field_type === FieldType.Radio) {
    if (checked) {
      emit('update:modelValue', option as RadioOption)
    } else if (isChecked(option, currentlyChecked)) {
      emit('update:modelValue', null)
    }
  }
}

const isFieldValid = computed(() => {
  const {
    field_type: fieldType,
    modelValue,
    type_settings: typeSettings
  } = typedProps.value

  if (fieldType === FieldType.Checkbox) {
    return !typeSettings.isMandatory || modelValue?.length > 0
  }

  if (fieldType === FieldType.Radio) {
    return !typeSettings.isMandatory || !!modelValue
  }

  if (fieldType === FieldType.RichText && typeof modelValue === 'string') {
    const min = typeSettings.characterLimitMin || 0
    const max = typeSettings.characterLimitMax || Infinity
    const textOnlyValue = striptags(modelValue)
    const length = textOnlyValue.length
    return length >= min && length <= max
  }

  if (fieldType === FieldType.Number) {
    return minMaxValueErrorText.value.length === 0
  }

  if (fieldType === FieldType.Datepicker) {
    return (
      !typedProps.value.type_settings.isMandatory ||
      (typedProps.value.modelValue && !isEmpty(typedProps.value.modelValue))
    )
  }

  return true
})

watch(
  isFieldValid,
  (val) => {
    if (
      ['checkbox', 'radio', 'datepicker', 'number', 'rich_text'].includes(
        typedProps.value.field_type
      )
    ) {
      emit('on-validate', props.definition.id, val)
    }
  },
  { immediate: true }
)

const handleFormInputValidation = (isValid: boolean) => {
  const valid = isValid && isFieldValid.value
  emit('on-validate', props.definition.id, valid)
}

return (_ctx: any,_cache: any) => {
  const _component_NrIcon = _resolveComponent("NrIcon")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (typedProps.value.field_type === _unref(FieldType).Text)
      ? (_openBlock(), _createBlock(NrFormInput, {
          key: 0,
          type: "text",
          modelValue: typedProps.value.modelValue,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = 
      (fieldValue) => emit('update:modelValue', fieldValue)
    ),
          tag: typedProps.value.type_settings.isTextArea ? 'textarea' : 'input',
          label: typedProps.value.type_settings.labelText,
          hideLabel: noLabel.value,
          class: _normalizeClass(noLabel.value ? 'request-field-no-label' : null),
          placeholder: typedProps.value.type_settings.placeholderText,
          help: typedProps.value.type_settings.helperText,
          required: typedProps.value.type_settings.isMandatory,
          maxLength: typedProps.value.type_settings.characterLimitMax?.toString(),
          minLength: typedProps.value.type_settings.characterLimitMin?.toString(),
          withTooltip: !_unref(isEmpty)(typedProps.value.type_settings.tooltipText),
          iconTooltipText: typedProps.value.type_settings.tooltipText,
          onOnValidate: handleFormInputValidation
        }, null, 8, ["modelValue", "tag", "label", "hideLabel", "class", "placeholder", "help", "required", "maxLength", "minLength", "withTooltip", "iconTooltipText"]))
      : _createCommentVNode("", true),
    (typedProps.value.field_type === _unref(FieldType).Number)
      ? (_openBlock(), _createBlock(NrFormInput, {
          key: 1,
          type: "number",
          modelValue: typedProps.value.modelValue,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = 
      (fieldValue) => {
        if (fieldValue === '') {
          emit('update:modelValue', null)
          return
        }
        typedProps.value.type_settings.allowDecimals
          ? emit('update:modelValue', fieldValue)
          : emit('update:modelValue', String(Math.floor(Number(fieldValue))))
      }
    ),
          label: typedProps.value.type_settings.labelText,
          class: _normalizeClass(noLabel.value ? 'request-field-no-label' : null),
          placeholder: typedProps.value.type_settings.placeholderText,
          help: typedProps.value.type_settings.helperText,
          required: typedProps.value.type_settings.isMandatory,
          withTooltip: !_unref(isEmpty)(typedProps.value.type_settings.tooltipText),
          iconTooltipText: typedProps.value.type_settings.tooltipText,
          step: typedProps.value.type_settings.allowDecimals ? 'any' : '1',
          onOnValidate: handleFormInputValidation
        }, null, 8, ["modelValue", "label", "class", "placeholder", "help", "required", "withTooltip", "iconTooltipText", "step"]))
      : _createCommentVNode("", true),
    (!isFieldValid.value && typedProps.value.field_type === _unref(FieldType).Number)
      ? (_openBlock(), _createElementBlock("p", _hoisted_1, _toDisplayString(minMaxValueErrorText.value), 1))
      : _createCommentVNode("", true),
    (typedProps.value.field_type === _unref(FieldType).RichText)
      ? (_openBlock(), _createBlock(NrEditor, {
          key: 3,
          modelValue: typedProps.value.modelValue,
          placeholder: typedProps.value.type_settings.placeholderText,
          required: typedProps.value.type_settings.isMandatory,
          resizable: typedProps.value.type_settings.isExpandable,
          maxLength: typedProps.value.type_settings.characterLimitMax?.toString(),
          minLength: typedProps.value.type_settings.characterLimitMin?.toString(),
          "ordered-list": true,
          "bullet-list": true,
          "has-tooltip": typedProps.value.type_settings.tooltipText.length,
          tooltip: typedProps.value.type_settings.tooltipText,
          label: 
      typedProps.value.type_settings.labelText
        ? typedProps.value.type_settings.labelText
        : ''
    ,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = (val) => emit('update:modelValue', val))
        }, null, 8, ["modelValue", "placeholder", "required", "resizable", "maxLength", "minLength", "has-tooltip", "tooltip", "label"]))
      : _createCommentVNode("", true),
    (
      !isFieldValid.value &&
      typedProps.value.field_type === _unref(FieldType).RichText &&
      (typedProps.value.type_settings.characterLimitMin ||
        typedProps.value.type_settings.characterLimitMax)
    )
      ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(minMaxErrorText.value
        ? `Minimum ${typedProps.value.type_settings.characterLimitMin} characters required.`
        : `Maximum ${typedProps.value.type_settings.characterLimitMax} characters exceeded.`), 1))
      : _createCommentVNode("", true),
    (typedProps.value.field_type === _unref(FieldType).Checkbox)
      ? (_openBlock(), _createElementBlock("fieldset", _hoisted_3, [
          (typedProps.value.type_settings.labelText)
            ? (_openBlock(), _createElementBlock("legend", {
                key: 0,
                class: _normalizeClass(noLabel.value ? 'is-sr-only' : 'checkbox-header')
              }, _toDisplayString(typedProps.value.type_settings.labelText) + " " + _toDisplayString(typedProps.value.type_settings.isMandatory ? '*' : ''), 3))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(typedProps.value.type_settings.options, (option) => {
            return (_openBlock(), _createElementBlock("div", {
              key: option.uuid,
              class: "checkbox-item"
            }, [
              _createVNode(NrFormInput, {
                type: "checkbox",
                label: option.name,
                modelValue: isChecked(option, typedProps.value.modelValue),
                "onUpdate:modelValue": 
          (checked) =>
            checkOption(option, checked, typedProps.value.modelValue)
        
              }, null, 8, ["label", "modelValue", "onUpdate:modelValue"])
            ]))
          }), 128)),
          (!isFieldValid.value && typedProps.value.field_type === _unref(FieldType).Checkbox)
            ? (_openBlock(), _createElementBlock("p", _hoisted_4, " At least one option must be selected. "))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    (typedProps.value.field_type === _unref(FieldType).Radio)
      ? (_openBlock(), _createElementBlock("fieldset", _hoisted_5, [
          (typedProps.value.type_settings.labelText)
            ? (_openBlock(), _createElementBlock("legend", {
                key: 0,
                class: _normalizeClass(noLabel.value ? 'is-sr-only' : 'radio-header')
              }, _toDisplayString(typedProps.value.type_settings.labelText) + " " + _toDisplayString(typedProps.value.type_settings.isMandatory ? '*' : ''), 3))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(typedProps.value.type_settings.options, (option) => {
            return (_openBlock(), _createElementBlock("div", {
              key: option.uuid,
              class: "radio-item"
            }, [
              _createVNode(NrFormInput, {
                type: "radio",
                name: `radio-${props.definition.id}`,
                label: option.name,
                modelValue: isChecked(option, typedProps.value.modelValue),
                "onUpdate:modelValue": 
          (checked) =>
            checkOption(option, checked, typedProps.value.modelValue)
        
              }, null, 8, ["name", "label", "modelValue", "onUpdate:modelValue"])
            ]))
          }), 128)),
          (!isFieldValid.value && typedProps.value.field_type === _unref(FieldType).Radio)
            ? (_openBlock(), _createElementBlock("p", _hoisted_6, " An option must be selected. "))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    (typedProps.value.field_type === _unref(FieldType).Datepicker)
      ? (_openBlock(), _createBlock(NrDatepicker, {
          key: 7,
          modelValue: typedProps.value.modelValue,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = 
      (fieldValue) => emit('update:modelValue', fieldValue)
    ),
          minDate: minDate.value
        }, {
          label: _withCtx(() => [
            (typedProps.value.type_settings.labelText)
              ? (_openBlock(), _createElementBlock("legend", {
                  key: 0,
                  class: _normalizeClass(noLabel.value ? 'is-sr-only' : 'checkbox-header')
                }, _toDisplayString(typedProps.value.type_settings.labelText) + " " + _toDisplayString(typedProps.value.type_settings.isMandatory ? '*' : ''), 3))
              : _createCommentVNode("", true)
          ]),
          trigger: _withCtx(() => [
            _createVNode(_component_NrIcon, { name: "calendar-alt" })
          ]),
          _: 1
        }, 8, ["modelValue", "minDate"]))
      : _createCommentVNode("", true),
    (!isFieldValid.value && typedProps.value.field_type === _unref(FieldType).Datepicker)
      ? (_openBlock(), _createElementBlock("p", _hoisted_7, " A date must be selected. "))
      : _createCommentVNode("", true)
  ], 64))
}
}

})